/**
 * SwmsPdfPreview.js — Full SWMS document rendered as A4 PDF pages.
 *
 * Hidden during normal viewing; shown only when capturing PDF.
 * Each chapter renders one or more <PdfPage> elements that are
 * located by capturePdfPages() via the [data-pdf-page] attribute.
 *
 * Props:
 *   swmsData     {object}  full SWMS document data
 *   projectInfo  {object}  { project_name, site_address, state, … }
 *   companyInfo  {object}  { name, acn, address, phone, email }
 */

import { useEffect } from 'react';
import PdfPage, { GOLD } from './PdfPage';

// ── Reusable PDF content helpers ──────────────────────────────────────────────

const styles = {
  h1: { fontSize: 16, fontWeight: 700, color: '#1a1a2e', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 },
  h2: { fontSize: 13, fontWeight: 600, color: GOLD, marginBottom: 3, letterSpacing: 0.3 },
  h3: { fontSize: 11, fontWeight: 600, color: '#1a1a2e', marginBottom: 2, marginTop: 4 },
  body: { fontSize: 10, color: '#1a1a2e', lineHeight: 1.5, marginBottom: 2 },
  muted: { fontSize: 9, color: '#7a7a8e' },
  label: { fontSize: 9, fontWeight: 600, color: '#6b6560', minWidth: 100 },
  value: { fontSize: 10, color: '#1a1a2e' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 9, marginTop: 2, marginBottom: 4 },
  th: { padding: '3px 5px', fontWeight: 600, color: '#fff', background: '#1a1a2e', textAlign: 'left', fontSize: 8, textTransform: 'uppercase' },
  td: { padding: '3px 5px', borderBottom: '1px solid #e0dbd0', color: '#1a1a2e', fontSize: 9, verticalAlign: 'middle' },
  section: { marginBottom: 6 },
  divider: { height: '1px', background: '#e0dbd0', margin: '4px 0' },
};

function P({ children, style }) {
  return <p style={{ ...styles.body, ...style }}>{children}</p>;
}

function H3({ children }) {
  return (
    <div style={{ marginTop: 6, marginBottom: 3 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>{children}</p>
      <div style={{ height: 1, background: GOLD, opacity: 0.6 }} />
    </div>
  );
}

function KvRow({ label, value }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid #e0dbd0', padding: '2px 0' }}>
      <span style={{ width: '30%', fontSize: 9, fontWeight: 600, color: '#6b6560', flexShrink: 0 }}>{label}</span>
      <span style={{ width: '70%', fontSize: 9, color: '#1a1a2e' }}>{value || '—'}</span>
    </div>
  );
}

function ChapterHeading({ number, title }) {
  return (
    <div style={{ marginBottom: 6 }}>
      {number && (
        <p style={{ fontSize: 9, color: GOLD, fontWeight: 600, marginBottom: 1, letterSpacing: 0.5 }}>
          OHSE-{number}
        </p>
      )}
      <p style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 0 }}>
        {title}
      </p>
      <div style={{ height: 1.5, background: GOLD, marginTop: 2, marginBottom: 6 }} />
    </div>
  );
}

function Table({ headers, rows, colWidths }) {
  if (!rows || rows.length === 0) return <p style={styles.muted}>No entries.</p>;
  return (
    <table style={styles.table}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={{ ...styles.th, width: colWidths?.[i] }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => (
              <td key={ci} style={styles.td}>{cell ?? '—'}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CheckList({ items, data, keyPrefix = '' }) {
  const checked = items.filter(item => data[`${keyPrefix}${item.key}`]);
  if (checked.length === 0) return <p style={styles.muted}>None selected.</p>;
  return (
    <div style={{ marginBottom: 4 }}>
      {checked.map(item => (
        <div key={item.key} style={{ display: 'flex', gap: 6, marginBottom: 1 }}>
          <span style={{ color: GOLD, fontSize: 9 }}>✓</span>
          <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function StaticPolicy({ companyName, text }) {
  const filled = text.replace(/\[Company Name\]/g, companyName || '[Company Name]');
  return <P>{filled}</P>;
}

// ── Chapter renderers ─────────────────────────────────────────────────────────

function CoverPage({ projectInfo, companyInfo }) {
  return (
    <PdfPage chapterCode="cover" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <div style={{
        minHeight: '220mm',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      }}>
        {/* Title */}
        <p style={{ fontSize: 28, fontWeight: 600, color: GOLD, letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 16 }}>
          OHSE Management Plan
        </p>

        {/* Gold divider */}
        <div style={{ width: 80, height: 2, background: GOLD, margin: '0 auto 24px' }} />

        {/* Project Name */}
        <h1 style={{ fontSize: 36, fontWeight: 700, color: '#1a1a2e', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 20, lineHeight: 1.2 }}>
          {projectInfo?.project_name || 'Project Name'}
        </h1>

        {/* Second divider */}
        <div style={{ width: 120, height: 1, background: GOLD, margin: '0 auto 24px' }} />

        {/* Company Name */}
        <p style={{ fontSize: 18, color: '#6b6560', letterSpacing: '0.06em', marginBottom: 6 }}>
          {companyInfo?.name || 'Organisation Name'}
        </p>

        {/* Contact details */}
        <div style={{ marginTop: 32, fontSize: 12, color: '#9a938b', lineHeight: 1.8 }}>
          {companyInfo?.address && <p>{companyInfo.address}</p>}
          <p>
            {companyInfo?.phone || ''}
            {companyInfo?.phone && companyInfo?.email ? '  |  ' : ''}
            {companyInfo?.email || ''}
          </p>
          {companyInfo?.acn && <p>ACN/ABN: {companyInfo.acn}</p>}
        </div>
      </div>
    </PdfPage>
  );
}

function ChapterProjectDetails({ projectInfo, companyInfo, swmsData }) {
  return (
    <PdfPage chapterCode="002" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="002" title="Project Details & Introduction" />
      <div style={styles.section}>
        <H3>Project Information</H3>
        <KvRow label="Project Name" value={projectInfo?.project_name} />
        <KvRow label="Site Address" value={projectInfo?.project_address} />
        <KvRow label="Client" value={projectInfo?.client_name} />
        <KvRow label="State" value={projectInfo?.state} />
        <KvRow label="Project Type" value={projectInfo?.project_type} />
        <KvRow label="Start Date" value={projectInfo?.project_start_date} />
        <KvRow label="End Date" value={projectInfo?.project_end_date} />
      </div>
      <div style={styles.section}>
        <H3>SWMS Details</H3>
        <KvRow label="Scope of Works" value={swmsData.scope_of_works} />
        <KvRow label="Principal Contractor" value={swmsData.principal_contractor} />
        <KvRow label="Site Manager" value={swmsData.site_manager_name} />
        <KvRow label="Site Manager Phone" value={swmsData.site_manager_phone} />
        <KvRow label="Site Manager Email" value={swmsData.site_manager_email} />
        <KvRow label="Number of Workers" value={swmsData.number_of_workers} />
        <KvRow label="Company Fax" value={swmsData.company_fax} />
      </div>
      <div style={styles.section}>
        <H3>Company Information</H3>
        <KvRow label="Company Name" value={companyInfo?.name} />
        <KvRow label="ABN/ACN" value={companyInfo?.acn} />
        <KvRow label="Address" value={companyInfo?.address} />
        <KvRow label="Phone" value={companyInfo?.phone} />
        <KvRow label="Email" value={companyInfo?.email} />
      </div>
    </PdfPage>
  );
}

function Chapter001({ swmsData, companyInfo }) {
  const revHistory = (swmsData.revision_history || []).map(r => [r.version || '', r.date || '', r.description || '', r.author || '']);
  const distList = (swmsData.distribution_list || []).map(d => [d.recipient_name || '', d.organisation || '', d.copy_type || '', d.date_issued || '']);

  return (
    <PdfPage chapterCode="001" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="001" title="Document Control" />
      <div style={styles.section}>
        <KvRow label="Document Version" value={swmsData.doc_version} />
        <KvRow label="Review Date" value={swmsData.doc_review_date} />
        <KvRow label="Next Review" value={swmsData.doc_next_review} />
        <KvRow label="Author" value={swmsData.doc_author} />
        <KvRow label="Approver" value={swmsData.doc_approver} />
        <KvRow label="Approval Date" value={swmsData.doc_approval_date} />
      </div>
      <div style={styles.section}>
        <H3>Revision History</H3>
        <Table headers={['Version', 'Date', 'Description', 'Author']} rows={revHistory} />
      </div>
      <div style={styles.section}>
        <H3>Distribution List</H3>
        <Table headers={['Recipient', 'Organisation', 'Copy Type', 'Date Issued']} rows={distList} />
      </div>
    </PdfPage>
  );
}

function Chapter003({ swmsData, companyInfo }) {
  const policy = swmsData.ohse_policy || {};
  return (
    <PdfPage chapterCode="003" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="003" title="OHSE & Environment Policy" />
      <P>
        {companyInfo?.name || '[Company Name]'} is committed to providing a safe and healthy work
        environment for all employees, contractors, visitors, and the community. We recognise that
        the effective management of Occupational Health, Safety, and Environmental (OHSE) risks is
        integral to our business success.
      </P>
      <P>
        Management is committed to: eliminating or minimising workplace hazards; consulting with
        workers on health and safety matters; providing appropriate information, instruction,
        training, and supervision; and continually improving OHSE performance through regular
        review of objectives and targets.
      </P>
      <div style={{ ...styles.divider, margin: '6px 0' }} />
      <KvRow label="Policy Date" value={policy.policy_date} />
      <KvRow label="Signed By" value={policy.signed_by} />
      <KvRow label="Title" value={policy.signatory_title} />
    </PdfPage>
  );
}

function Chapter004({ companyInfo }) {
  return (
    <PdfPage chapterCode="004" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="004" title="Hazard Identification, Risk Assessment & Control" />
      <P>
        {companyInfo?.name || '[Company Name]'} has implemented a systematic process for hazard
        identification, risk assessment, and control. This process is consistent with the hierarchy
        of control as outlined in relevant legislation and Australian Standards.
      </P>
      <H3>Hierarchy of Control</H3>
      <div style={{ paddingLeft: 8 }}>
        {['Elimination — Remove the hazard entirely',
          'Substitution — Replace with something safer',
          'Isolation — Separate people from the hazard',
          'Engineering Controls — Physical safeguards',
          'Administrative Controls — Policies, training, signage',
          'Personal Protective Equipment — Last line of defence',
        ].map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
            <span style={{ color: GOLD, fontSize: 9 }}>{i + 1}.</span>
            <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item}</span>
          </div>
        ))}
      </div>
      <P style={{ marginTop: 6 }}>
        All identified hazards are documented in the Hazard Categories (OHSE 005) and Safe Work
        Method Statements (OHSE 007). Risk levels are determined using the Risk Matrix (OHSE 006)
        based on likelihood and consequence.
      </P>
    </PdfPage>
  );
}

function Chapter005({ swmsData, companyInfo }) {
  const hazardCats = swmsData.hazard_categories || {};

  const OHS_ITEMS = [
    { key: 'access_egress', label: 'Access & egress' },
    { key: 'confined_enclosed_spaces', label: 'Confined/enclosed spaces' },
    { key: 'coring_chasing', label: 'Coring/chasing' },
    { key: 'dangerous_goods', label: 'Dangerous Goods (Oxy/other)' },
    { key: 'demolition_dismantling', label: 'Demolition/dismantling' },
    { key: 'electricity', label: 'Electricity (power tools/other)' },
    { key: 'explosive_pneumatic_tools', label: 'Explosive/pneumatic power tools' },
    { key: 'fatigue', label: 'Fatigue (shift work/hours of work)' },
    { key: 'formwork', label: 'Formwork erection/dismantling' },
    { key: 'fire_explosion', label: 'Fire/explosion' },
    { key: 'fumes_gas', label: 'Fumes/gas' },
    { key: 'hazardous_substances', label: 'Hazardous substances' },
    { key: 'height_falls', label: 'Height & falls' },
    { key: 'hot_cold_environment', label: 'Hot/cold working environment' },
    { key: 'hot_work', label: 'Hot work (cutting/welding/grinding)' },
    { key: 'manual_handling', label: 'Manual handling' },
    { key: 'noise_hearing', label: 'Noise (hearing)' },
    { key: 'services_underground_overhead', label: 'Services (underground/overhead)' },
  ];

  const ENV_ITEMS = [
    { key: 'air_quality', label: 'Air quality (dust/emissions)' },
    { key: 'contaminated_soil_water', label: 'Contaminated soil/water' },
    { key: 'dewatering', label: 'Dewatering/pump out' },
    { key: 'noise_vibration', label: 'Noise or vibration' },
    { key: 'spills_response', label: 'Spills & response' },
    { key: 'waste_hazardous', label: 'Waste hazardous' },
    { key: 'stormwater_sediment', label: 'Stormwater/sediment control' },
  ];

  const customOhs = (hazardCats.custom_ohs || []).filter(c => c.checked);
  const customEnv = (hazardCats.custom_env || []).filter(c => c.checked);

  return (
    <PdfPage chapterCode="005" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="005" title="Hazard Categories" />
      <P>
        The following hazards have been identified by {companyInfo?.name || '[Company Name]'} for
        the contracted work activities. These hazards are addressed in the Safe Work Method Statement(s).
      </P>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <div style={{ flex: 1 }}>
          <H3>Occupational Health & Safety</H3>
          <CheckList items={OHS_ITEMS} data={hazardCats} />
          {customOhs.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 6 }}>
              <span style={{ color: GOLD, fontSize: 9 }}>✓</span>
              <span style={{ fontSize: 9, color: '#1a1a2e' }}>{c.label}</span>
            </div>
          ))}
        </div>
        <div style={{ flex: 1 }}>
          <H3>Environment</H3>
          <CheckList items={ENV_ITEMS} data={hazardCats} />
          {customEnv.map(c => (
            <div key={c.id} style={{ display: 'flex', gap: 6 }}>
              <span style={{ color: GOLD, fontSize: 9 }}>✓</span>
              <span style={{ fontSize: 9, color: '#1a1a2e' }}>{c.label}</span>
            </div>
          ))}
        </div>
      </div>
    </PdfPage>
  );
}

function Chapter007({ swmsData, companyInfo }) {
  const steps = swmsData.swms_steps || [];
  const stepRows = steps.filter(s => s.step_description || s.hazards).map(s => [
    s.step_number || '',
    s.step_description || '',
    s.hazards || '',
    s.risk_level ? (s.risk_level === 1 ? 'High' : s.risk_level === 2 ? 'Medium' : 'Low') : '',
    s.control_measures || '',
    s.responsible_person || '',
  ]);

  return (
    <PdfPage chapterCode="007" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="007" title="Safe Work Method Statement" />
      <KvRow label="Project" value={swmsData.scope_of_works} />
      <div style={styles.divider} />
      {stepRows.length > 0 ? (
        <Table
          headers={['Step', 'Description', 'Hazards', 'Risk', 'Control Measures', 'Responsible']}
          rows={stepRows}
          colWidths={['10%', '20%', '18%', '8%', '30%', '14%']}
        />
      ) : (
        <P style={styles.muted}>No work steps defined. Add SWMS steps in the editor.</P>
      )}
    </PdfPage>
  );
}

function Chapter008({ swmsData, companyInfo }) {
  const objectives = swmsData.objectives_targets || [];
  const rows = objectives.map(o => [o.objective || '', o.target || '', o.responsible || '', o.timeframe || '', o.status || '']);

  return (
    <PdfPage chapterCode="008" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="008" title="Objectives & Targets" />
      <P>
        {companyInfo?.name || '[Company Name]'} has established the following OHSE objectives and targets
        to drive continuous improvement in health, safety, and environmental performance.
      </P>
      {rows.length > 0 ? (
        <Table headers={['Objective', 'Target', 'Responsible', 'Timeframe', 'Status']} rows={rows} />
      ) : (
        <P style={styles.muted}>No objectives defined.</P>
      )}
    </PdfPage>
  );
}

function Chapter009({ swmsData, companyInfo }) {
  const ppe = swmsData.ppe || {};
  const PPE_ITEMS = [
    { key: 'steel_capped_boots', label: 'Steel Capped Boots' },
    { key: 'hi_visibility_clothing', label: 'High Visibility Clothing' },
    { key: 'hard_hat', label: 'Hard Hat' },
    { key: 'dust_mask', label: 'Dust Mask' },
    { key: 'face_shield', label: 'Face Shield' },
    { key: 'gloves', label: 'Gloves' },
    { key: 'safety_glasses', label: 'Safety Glasses' },
    { key: 'hearing_protection', label: 'Hearing Protection' },
  ];
  const register = (ppe.ppe_register || []).map(e => [e.employee_name || '', e.date || '', e.notes || '']);

  return (
    <PdfPage chapterCode="009" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="009" title="Personal Protective Equipment" />
      <P>The following PPE is required for the project activities:</P>
      <CheckList items={PPE_ITEMS} data={ppe} />
      {register.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <H3>PPE Issue Register</H3>
          <Table headers={['Employee Name', 'Date', 'Notes']} rows={register} />
        </div>
      )}
    </PdfPage>
  );
}

function Chapter010({ swmsData, companyInfo }) {
  const roles = swmsData.roles_responsibilities || [];
  const empResp = swmsData.employee_responsibilities || [];

  return (
    <PdfPage chapterCode="010" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="010" title="Roles & Responsibilities" />
      <P>
        Clearly defined roles and responsibilities are essential for effective OHSE management.
      </P>
      {roles.length > 0 && (
        <div style={styles.section}>
          <H3>Key Roles</H3>
          {roles.map((r, i) => (
            <div key={i} style={{ marginBottom: 3, padding: '3px 0', borderBottom: '1px solid #e0dbd0' }}>
              <p style={{ fontSize: 10, fontWeight: 600, color: '#1a1a2e' }}>{r.role || r.title || `Role ${i + 1}`}</p>
              <p style={{ fontSize: 9, color: '#6b6560' }}>{r.responsibilities || r.description || r.name || ''}</p>
            </div>
          ))}
        </div>
      )}
      {empResp.length > 0 && (
        <div style={styles.section}>
          <H3>Employee Responsibilities</H3>
          {empResp.map((e, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 1 }}>
              <span style={{ color: GOLD, fontSize: 9 }}>•</span>
              <span style={{ fontSize: 9, color: '#1a1a2e' }}>{typeof e === 'string' ? e : e.responsibility || e.description || ''}</span>
            </div>
          ))}
        </div>
      )}
    </PdfPage>
  );
}

function Chapter011({ swmsData, companyInfo }) {
  const entries = (swmsData.training_entries || []).map(e => [e.employee_name || '', e.training_type || e.course || '', e.date || '', e.expiry_date || '', e.notes || '']);
  return (
    <PdfPage chapterCode="011" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="011" title="Training & Competency Register" />
      <P>
        All personnel working on site must hold appropriate training and competency certifications.
        Records are maintained and reviewed regularly.
      </P>
      {entries.length > 0 ? (
        <Table headers={['Name', 'Training', 'Date', 'Expiry', 'Notes']} rows={entries} />
      ) : (
        <P style={styles.muted}>No training records entered.</P>
      )}
    </PdfPage>
  );
}

function Chapter012({ companyInfo }) {
  return (
    <PdfPage chapterCode="012" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="012" title="Consultation & Communication" />
      <P>
        {companyInfo?.name || '[Company Name]'} recognises that consultation with workers and
        their representatives is a legal requirement and a key element of effective OHSE management.
      </P>
      <H3>Communication Methods</H3>
      {[
        'Toolbox talks and pre-start briefings (recorded in OHSE 013)',
        'Safety committee meetings',
        'Noticeboard displays and safety alerts',
        'Induction and training sessions',
        'Incident notification and investigation debriefs',
        'SWMS review and sign-off process',
      ].map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: GOLD, fontSize: 9 }}>•</span>
          <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item}</span>
        </div>
      ))}
    </PdfPage>
  );
}

function Chapter013({ swmsData, companyInfo }) {
  const talks = (swmsData.toolbox_talks || []).map(t => [t.date || '', t.topic || '', t.presenter || '', t.duration || '', String(t.attendees || t.attendee_count || '')]);
  return (
    <PdfPage chapterCode="013" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="013" title="Toolbox Talks / Pre-Start Briefings" />
      <P>Record of daily/weekly toolbox talks and pre-start safety briefings.</P>
      {talks.length > 0 ? (
        <Table headers={['Date', 'Topic', 'Presenter', 'Duration', 'Attendees']} rows={talks} />
      ) : (
        <P style={styles.muted}>No toolbox talks recorded.</P>
      )}
    </PdfPage>
  );
}

function Chapter014({ swmsData, companyInfo }) {
  const inspections = (swmsData.workplace_inspections || []).map(i => [i.date || '', i.inspector || '', i.area || '', i.findings || '', i.status || '', String(i.items?.length || i.action_items?.length || '')]);
  return (
    <PdfPage chapterCode="014" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="014" title="Workplace Inspection Checklist" />
      <P>Regular workplace inspections are conducted to identify hazards and ensure controls remain effective.</P>
      {inspections.length > 0 ? (
        <Table headers={['Date', 'Inspector', 'Area', 'Findings', 'Status', 'Actions']} rows={inspections} />
      ) : (
        <P style={styles.muted}>No inspections recorded.</P>
      )}
    </PdfPage>
  );
}

function Chapter015({ companyInfo }) {
  return (
    <PdfPage chapterCode="015" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="015" title="Plant & Equipment Policy" />
      <P>
        {companyInfo?.name || '[Company Name]'} is committed to ensuring all plant and equipment
        used on site is safe and fit for purpose. This policy outlines the requirements for the
        management of plant and equipment throughout their lifecycle.
      </P>
      <H3>Key Requirements</H3>
      {[
        'All plant and equipment must be registered and tagged before use',
        'Pre-start inspections must be completed each shift',
        'Regular inspections must be conducted as per manufacturer specifications',
        'Maintenance records must be kept for the life of the equipment',
        'Only trained and authorised personnel may operate plant and equipment',
        'Defective equipment must be immediately removed from service and tagged "Out of Service"',
      ].map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: GOLD, fontSize: 9 }}>•</span>
          <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item}</span>
        </div>
      ))}
    </PdfPage>
  );
}

function Chapter016({ swmsData, companyInfo }) {
  const entries = (swmsData.equipment_entries || []).map(e => [
    e.plant_type || '',
    e.make_model || '',
    e.serial_registration_number || '',
    e.last_service_date || '',
    e.maintenance_frequency || '',
    e.logbook_available ? 'Yes' : 'No',
  ]);
  return (
    <PdfPage chapterCode="016" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="016" title="Plant & Equipment Register" />
      <P>Register of all plant and equipment on site.</P>
      {entries.length > 0 ? (
        <Table headers={['Plant Type', 'Make / Model', 'Serial / Reg No.', 'Last Service', 'Maint. Frequency', 'Logbook']} rows={entries} />
      ) : (
        <P style={styles.muted}>No equipment registered.</P>
      )}
    </PdfPage>
  );
}

function Chapter017({ swmsData, companyInfo }) {
  const checks = swmsData.equipment_prestart_check || {};
  const itemList = Array.isArray(checks.checklist_items) ? checks.checklist_items : [];
  const plantInspected = Array.isArray(checks.plant_inspected) ? checks.plant_inspected : [];

  return (
    <PdfPage chapterCode="017" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="017" title="Plant & Equipment Pre-Start Inspection" />
      <P>Pre-start inspections must be completed before any plant or equipment is used each shift.</P>
      {itemList.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <H3>Pre-Start Checklist</H3>
          {itemList.map((item, i) => {
            const status = item.yes ? '✓ Yes' : item.no ? '✗ No' : '—';
            const statusColor = item.yes ? '#10b981' : item.no ? '#ef4444' : '#9ca3af';
            return (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 1, padding: '2px 0', borderBottom: '1px solid #f0ede6' }}>
                <span style={{ color: statusColor, fontSize: 9, width: 36, flexShrink: 0 }}>{status}</span>
                <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item.item || `Item ${i + 1}`}</span>
              </div>
            );
          })}
        </div>
      )}
      {plantInspected.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <H3>Plant Inspected</H3>
          <Table
            headers={['Plant Name', 'Plant ID / Serial', 'Company']}
            rows={plantInspected.map(p => [p.plant_name || '', p.plant_id || '', p.company || ''])}
          />
        </div>
      )}
      <div style={{ marginTop: 6 }}>
        <H3>Plant Provider</H3>
        <KvRow label="Name" value={checks.plant_provider_name} />
        <KvRow label="Date" value={checks.plant_provider_date} />
        <H3>Verified By</H3>
        <KvRow label="Name" value={checks.verified_by_name} />
        <KvRow label="Date" value={checks.verified_by_date} />
      </div>
    </PdfPage>
  );
}

function Chapter018({ swmsData, companyInfo }) {
  const checks = swmsData.equipment_periodic_check || {};
  const itemList = Array.isArray(checks.checklist_items) ? checks.checklist_items : [];

  return (
    <PdfPage chapterCode="018" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="018" title="Plant & Equipment Regular Inspection" />
      <P>Regular periodic inspections ensure ongoing safe operation of plant and equipment.</P>
      <KvRow label="Service Provider" value={checks.service_provider_name} />
      <KvRow label="Plant Type / Make" value={checks.plant_type_make} />
      <KvRow label="Plant No." value={checks.plant_no} />
      <KvRow label="Serial No." value={checks.serial_no} />
      {itemList.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <H3>Checklist Items</H3>
          {itemList.map((item, i) => {
            const status = item.yes ? '✓ Yes' : item.no ? '✗ No' : item.na ? 'N/A' : '—';
            const statusColor = item.yes ? '#10b981' : item.no ? '#ef4444' : item.na ? '#6b6560' : '#9ca3af';
            return (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 1, padding: '2px 0', borderBottom: '1px solid #f0ede6' }}>
                <span style={{ color: statusColor, fontSize: 9, width: 36, flexShrink: 0 }}>{status}</span>
                <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item.description || `Item ${i + 1}`}</span>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 6 }}>
        <H3>Plant Provider</H3>
        <KvRow label="Name" value={checks.plant_provider_name} />
        <KvRow label="Date" value={checks.plant_provider_date} />
        <H3>Verified By</H3>
        <KvRow label="Name" value={checks.verified_by_name} />
        <KvRow label="Date" value={checks.verified_by_date} />
      </div>
    </PdfPage>
  );
}

function Chapter019({ companyInfo }) {
  return (
    <PdfPage chapterCode="019" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="019" title="Hazardous Substances / Dangerous Goods Policy" />
      <P>
        {companyInfo?.name || '[Company Name]'} is committed to ensuring all hazardous substances
        and dangerous goods are stored, handled, and used in accordance with relevant legislation,
        Australian Standards, and manufacturer instructions.
      </P>
      <H3>Key Requirements</H3>
      {[
        'A register of all hazardous substances on site must be maintained (OHSE 020)',
        'Safety Data Sheets (SDS) must be readily accessible at the point of use',
        'All containers must be correctly labelled',
        'Spill containment and emergency response equipment must be available',
        'Staff handling hazardous substances must receive appropriate training',
        'Dangerous goods must be stored in accordance with relevant regulations',
      ].map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: GOLD, fontSize: 9 }}>•</span>
          <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item}</span>
        </div>
      ))}
    </PdfPage>
  );
}

function Chapter020({ swmsData, companyInfo }) {
  const register = (swmsData.hazardous_substance_register || []).map(s => [s.substance_name || s.name || '', s.location || '', s.quantity || '', s.risk_phrase || s.risk || '', s.sds_date || '']);
  return (
    <PdfPage chapterCode="020" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="020" title="Hazardous Substance Register" />
      <P>Register of all hazardous substances used or stored on site.</P>
      {register.length > 0 ? (
        <Table headers={['Substance', 'Location', 'Quantity', 'Risk Phrase', 'SDS Date']} rows={register} />
      ) : (
        <P style={styles.muted}>No hazardous substances registered.</P>
      )}
    </PdfPage>
  );
}

function Chapter021({ companyInfo }) {
  return (
    <PdfPage chapterCode="021" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="021" title="Electrical Equipment Policy" />
      <P>
        {companyInfo?.name || '[Company Name]'} requires all electrical equipment used on site to
        be maintained in a safe condition and inspected/tested in accordance with relevant standards.
      </P>
      <H3>Key Requirements</H3>
      {[
        'All electrical equipment must be registered (OHSE 022)',
        'Test and Tag records must be current and available for inspection',
        'Portable RCDs (safety switches) must be tested before each use',
        'Damaged or faulty equipment must be immediately removed from service',
        'Only licensed electricians may perform electrical work',
        'Lead and extension cords must be run safely to avoid trip hazards and damage',
      ].map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: GOLD, fontSize: 9 }}>•</span>
          <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item}</span>
        </div>
      ))}
    </PdfPage>
  );
}

function Chapter022({ swmsData, companyInfo }) {
  const register = (swmsData.electrical_equipment_register || []).map(e => [e.equipment_name || e.name || '', e.type || '', e.test_date || '', e.next_test_date || '', e.status || '']);
  return (
    <PdfPage chapterCode="022" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="022" title="Electrical Equipment Register" />
      <P>Register of all electrical equipment on site, including test and tag records.</P>
      {register.length > 0 ? (
        <Table headers={['Equipment', 'Type', 'Last Test', 'Next Test', 'Status']} rows={register} />
      ) : (
        <P style={styles.muted}>No electrical equipment registered.</P>
      )}
    </PdfPage>
  );
}

function Chapter025({ companyInfo }) {
  return (
    <PdfPage chapterCode="025" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="025" title="Incident & Injury Investigation Policy" />
      <P>
        {companyInfo?.name || '[Company Name]'} is committed to the prompt reporting and thorough
        investigation of all workplace incidents, injuries, and near misses. The goal is to identify
        root causes and implement corrective actions to prevent recurrence.
      </P>
      <H3>Incident Reporting Procedure</H3>
      {[
        'All incidents, injuries, and near misses must be reported immediately to the site supervisor',
        'First aid or medical treatment to be provided as required',
        'The incident scene must be secured for investigation',
        'An Incident Investigation Report (OHSE 027) must be completed within 24 hours',
        'Corrective actions must be implemented and monitored for effectiveness',
        'All incidents are recorded in the Register of Injuries (OHSE 026)',
      ].map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
          <span style={{ color: GOLD, fontSize: 9 }}>•</span>
          <span style={{ fontSize: 9, color: '#1a1a2e' }}>{item}</span>
        </div>
      ))}
    </PdfPage>
  );
}

function Chapter026({ swmsData, companyInfo }) {
  const register = (swmsData.injury_register || []).map(i => [i.date || '', i.employee_name || '', i.injury_type || i.description || '', i.treatment || '', i.status || '']);
  return (
    <PdfPage chapterCode="026" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="026" title="Register of Injuries" />
      <P>Record of all workplace injuries and incidents.</P>
      {register.length > 0 ? (
        <Table headers={['Date', 'Employee', 'Injury', 'Treatment', 'Status']} rows={register} />
      ) : (
        <P style={styles.muted}>No injuries recorded.</P>
      )}
    </PdfPage>
  );
}

function Chapter027({ swmsData, companyInfo }) {
  const investigations = (swmsData.incident_investigations || []).map(inv => [inv.date || '', inv.incident_description || inv.description || '', inv.root_cause || inv.cause || '', inv.corrective_actions || inv.actions || '', inv.status || '']);
  return (
    <PdfPage chapterCode="027" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="027" title="Incident Investigation Report" />
      <P>Reports of investigations into workplace incidents, injuries, and near misses.</P>
      {investigations.length > 0 ? (
        <Table headers={['Date', 'Incident', 'Root Cause', 'Corrective Actions', 'Status']} rows={investigations} />
      ) : (
        <P style={styles.muted}>No investigations recorded.</P>
      )}
    </PdfPage>
  );
}

function Chapter031({ swmsData, companyInfo }) {
  const rules = swmsData.site_rules || [];
  const contacts = swmsData.emergency_contacts || [];
  const contactRows = contacts.map(c => [c.name || c.role || '', c.phone || '', c.organisation || '', c.notes || '']);

  return (
    <PdfPage chapterCode="031" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="031" title="Site Rules & Emergency Contacts" />
      <div style={styles.section}>
        {rules.length > 0 ? (
          rules.map((rule, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 2 }}>
              <span style={{ color: GOLD, fontSize: 9 }}>•</span>
              <span style={{ fontSize: 9, color: '#1a1a2e' }}>{typeof rule === 'string' ? rule : rule.rule || rule.description || ''}</span>
            </div>
          ))
        ) : (
          <P style={styles.muted}>No site rules defined.</P>
        )}
      </div>
      <div style={styles.section}>
        <H3>Emergency Contacts</H3>
        {contactRows.length > 0 ? (
          <Table headers={['Name/Role', 'Phone', 'Organisation', 'Notes']} rows={contactRows} />
        ) : (
          <P style={styles.muted}>No emergency contacts entered.</P>
        )}
      </div>
      <div style={{ ...styles.divider, margin: '6px 0' }} />
      <KvRow label="Nearest Hospital" value={swmsData.nearest_hospital} />
      <KvRow label="Assembly Point" value={swmsData.assembly_point} />
    </PdfPage>
  );
}

function Chapter006({ companyInfo }) {
  const cn = companyInfo?.name || '[Company Name]';
  const CONSEQUENCE_TABLE = [
    { level: 'High (1)', subtitle: 'High level of harm', description: 'Potential death, permanent disability or major structural failure/damage. Off-site environmental discharge/release not contained and significant long-term environmental harm.' },
    { level: 'Medium (2)', subtitle: 'Medium level of harm', description: 'Potential temporary disability or minor structural failure/damage. On-site environmental discharge/release contained, minor remediation required, short-term environmental harm.' },
    { level: 'Low (3)', subtitle: 'Low level of harm', description: 'Incident that has the potential to cause persons to require first aid. On-site environmental discharge/release immediately contained, minor level clean up with no short-term environmental harm.' },
  ];
  const LIKELIHOOD_TABLE = [
    { level: 'Likely', description: 'Could happen frequently' },
    { level: 'Moderate', description: 'Could happen occasionally' },
    { level: 'Unlikely', description: 'May occur only in exceptional circumstances.' },
  ];
  const RANKING_TABLE = [
    { class: 1, description: 'Will require detailed pre-planning.\nActions will be recorded on a Safe Work Method Statement', color: '#ef4444' },
    { class: 2, description: 'Will require operational planning.\nActions will be recorded on a Safe Work Method Statement', color: '#fbbf24' },
    { class: 3, description: 'Will require localised control measures', color: '#22c55e' },
  ];
  const LIKELIHOOD_OPTIONS = [
    { label: 'Likely' }, { label: 'Moderate' }, { label: 'Unlikely' },
  ];
  const CONSEQUENCES_OPTIONS = [
    { label: 'High' }, { label: 'Medium' }, { label: 'Low' },
  ];
  const MATRIX = [
    ['High', 'High', 'Medium'],
    ['High', 'Medium', 'Low'],
    ['Medium', 'Low', 'Low'],
  ];
  const CELL_COLORS = { High: '#ef4444', Medium: '#fbbf24', Low: '#22c55e' };

  return (
    <PdfPage chapterCode="006" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="006" title="Risk Matrix" />
      <P style={{ marginBottom: 6 }}>
        {cn} has identified a risk class/ranking for potential workplace hazards by referring to the categories in the matrix below.
      </P>

      <P style={{ marginTop: 10, marginBottom: 8 }}><span style={{ color: GOLD, fontWeight: 700 }}>Step 1:</span> The organisation identifies the consequence for each potential risk by using the table below. <span style={{ fontStyle: 'italic' }}>Note: If a combination of harm, loss or damage could occur the worst case consequence is selected.</span></P>
      <div style={{ marginBottom: 8 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '80px' }}>Level</th>
              <th style={styles.th}>Description of Consequence</th>
            </tr>
          </thead>
          <tbody>
            {CONSEQUENCE_TABLE.map((row, i) => (
              <tr key={i}>
                <td style={{ ...styles.td, fontWeight: 600, background: '#f8f7f4' }}>
                  {row.level}<br /><span style={{ fontWeight: 400, color: '#6b6560', fontSize: 8 }}>({row.subtitle})</span>
                </td>
                <td style={styles.td}>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <P style={{ marginTop: 10, marginBottom: 8 }}><span style={{ color: GOLD, fontWeight: 700 }}>Step 2:</span> Using the following table, the organisation determines how likely it is that the risk will occur.</P>
      <div style={{ marginBottom: 8 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '70px' }}>Level</th>
              <th style={styles.th}>Likelihood / Probability</th>
            </tr>
          </thead>
          <tbody>
            {LIKELIHOOD_TABLE.map((row, i) => (
              <tr key={i}>
                <td style={{ ...styles.td, fontWeight: 600, background: '#f8f7f4' }}>{row.level}</td>
                <td style={styles.td}>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <P style={{ marginTop: 10, marginBottom: 8 }}><span style={{ color: GOLD, fontWeight: 700 }}>Step 3:</span> Using the risk matrix below, the organisation identifies the risk class/ranking.</P>
      <div style={{ marginBottom: 8 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '80px' }}>Consequence ↓ / Likelihood →</th>
              {LIKELIHOOD_OPTIONS.map((opt, i) => (
                <th key={i} style={{ ...styles.th, textAlign: 'center' }}>{opt.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MATRIX.map((row, ri) => (
              <tr key={ri}>
                <td style={{ ...styles.td, fontWeight: 500, background: '#f8f7f4' }}>{CONSEQUENCES_OPTIONS[ri].label}</td>
                {row.map((rating, ci) => (
                  <td key={ci} style={{ ...styles.td, textAlign: 'center', fontWeight: 700, fontSize: 11, color: '#000', background: CELL_COLORS[rating] }}>
                    {rating === 'High' ? 1 : rating === 'Medium' ? 2 : 3}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 10 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '80px' }}>Class/Ranking</th>
              <th style={styles.th}>Description / Requirements</th>
            </tr>
          </thead>
          <tbody>
            {RANKING_TABLE.map(row => (
              <tr key={row.class}>
                <td style={{ ...styles.td, textAlign: 'center', fontWeight: 700, fontSize: 11, color: '#000', background: row.color }}>{row.class}</td>
                <td style={{ ...styles.td, whiteSpace: 'pre-line', lineHeight: 1.4 }}>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PdfPage>
  );
}

function Chapter023({ companyInfo }) {
  const cn = companyInfo?.name || '[Company Name]';
  return (
    <PdfPage chapterCode="023" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="023" title="Hazard Reporting" />
      <P style={{ marginBottom: 10 }}>
        {cn} encourages all employees to report hazards{' '}
        <span style={{ textDecoration: 'underline' }}>immediately</span> to the Works Supervisor.
      </P>
      <P style={{ marginBottom: 10 }}>
        Where the hazard cannot be corrected immediately, {cn} records the details of the
        hazard in the Hazard Register.
      </P>
      <P style={{ marginBottom: 10 }}>
        {cn} investigates all reported hazards and implements control measures to eliminate
        and/or minimise the likelihood of an incident or injury.
      </P>
      <P style={{ marginBottom: 10 }}>
        {cn} identifies a risk class/ranking for all hazards by referring to the categories
        ranging from high to low in the Risk Matrix. The Risk Matrix is used to determine the
        level of danger or seriousness (i.e. the consequence) of the risk, how likely it is
        that this risk will occur (i.e. likelihood/probability) and therefore how detailed
        control measures will need to be to eliminate or minimise the risk.
      </P>
      <P style={{ marginBottom: 10 }}>
        {cn} regularly reviews and evaluates the effectiveness of control measures until the
        hazard is addressed and/or all risks have been mitigated or reduced.
      </P>
      <P style={{ marginBottom: 0 }}>
        {cn} will issue a copy of any completed Hazard Report form to the principal contractor,
        as required.
      </P>
    </PdfPage>
  );
}

function Chapter024({ swmsData, companyInfo }) {
  const cn = companyInfo?.name || '[Company Name]';
  const entries = swmsData.hazard_reports || [];
  const RISK_CLASSES = [
    { value: '1', label: 'High (1)' },
    { value: '2', label: 'Medium (2)' },
    { value: '3', label: 'Low (3)' },
  ];
  const WHEN_OPTIONS = [
    { value: 'Immediate', label: 'Immediate' },
    { value: 'Within 24 hrs', label: 'Within 24 hrs' },
    { value: 'Within 7 Days', label: 'Within 7 Days' },
  ];

  if (entries.length === 0) {
    return (
      <PdfPage chapterCode="024" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
        <ChapterHeading number="024" title="Hazard Report Form" />
        <P>No hazard reports recorded.</P>
      </PdfPage>
    );
  }

  return entries.map((entry, idx) => {
    const riskLabel = RISK_CLASSES.find(r => r.value === entry.risk_class)?.label || '';
    const whenLabel = WHEN_OPTIONS.find(w => w.value === entry.when_required)?.label || '';

    return (
      <PdfPage key={entry.id} chapterCode="024" companyName={companyInfo?.name} companyAbn={companyInfo?.acn} pageNum={idx + 1} total={entries.length}>
        <ChapterHeading number="024" title="Hazard Report Form" />
        <P>{cn} records the hazard in the Hazard Report.</P>

        <H3>General</H3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <KvRow label="Date" value={entry.date} />
          <KvRow label="Workplace" value={entry.workplace} />
          <KvRow label="Submitted By" value={entry.submitted_by} />
        </div>
        <KvRow label="Submitted To" value={entry.submitted_to} />

        <H3>Details of Hazard</H3>
        <KvRow label="Location" value={entry.location} />
        <KvRow label="Work Activity" value={entry.work_activity} />
        <P style={{ marginBottom: 2 }}>Hazard identified: {entry.hazard_identified || '—'}</P>

        <H3>Details of Risk</H3>
        <P>Risk Class: {riskLabel || '—'}</P>

        <H3>Control Measures</H3>
        <P style={{ marginBottom: 2 }}>Corrective Action: {entry.corrective_action || '—'}</P>
        <KvRow label="By Whom" value={entry.action_by} />
        <KvRow label="When" value={whenLabel} />

        <H3>Completion</H3>
        <KvRow label="Completed By" value={entry.corrective_action_completed_by} />
        <KvRow label="Time" value={entry.completion_time} />
        <KvRow label="Date" value={entry.completion_date} />
        <KvRow label="Confirmed By" value={entry.confirmed_by} />
      </PdfPage>
    );
  });
}

function Chapter028({ swmsData, companyInfo }) {
  const cn = companyInfo?.name || '[Company Name]';
  const data = swmsData.ohse_checklist || {};
  const items = data.items || [];
  const CHECKLIST_ITEMS = [
    'Changes and distribution of the OHSE Mgt Plan are recorded',
    'Project details / Description of works / Organisation details are current',
    'OHSE Policy signed and dated by Director/Manager',
    'Hazards are IDENTIFIED, and risks are assessed',
    'Controls for high risk activities are documented (Safe Work Method Statement(s))',
    'Training and Competency Register is current',
    'Site Specific Induction Training records are current',
    'SWMS Training is current',
    'Roles and responsibilities are allocated and signed',
    'Consultation arrangements (nature, topics, intervals) are documented',
    'Plant / Equipment Register is current',
    'Hazardous Substances / Dangerous Goods Register is current',
    'Personal Protective Equipment Register is current',
    'Periodic Workplace Inspection Checklists are completed',
    'Register of Injuries is current',
    'Incident Investigation Reports are completed',
    'Hazard Reports are completed',
    'Electrical Equipment Register is current',
    'Injury Management and Return-to-Work Program is displayed',
    'Workers Compensation Information is current',
    'Other',
  ];

  if (items.length === 0) {
    return (
      <PdfPage chapterCode="028" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
        <ChapterHeading number="028" title="OHSE Management Plan Checklist" />
        <P>No checklist data recorded.</P>
      </PdfPage>
    );
  }

  return (
    <PdfPage chapterCode="028" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="028" title="OHSE Management Plan Checklist" />
      <P>{cn} reviews all OHSE policies and procedures every 3 months.</P>
      <KvRow label="Location" value={data.location} />
      <KvRow label="Auditor" value={data.auditor} />
      <KvRow label="Attendees" value={data.other_attendees} />

      <H3>Activities Reviewed</H3>
      <div style={{ display: 'flex', gap: 4, paddingBottom: 3, borderBottom: `1px solid ${GOLD}`, marginBottom: 2 }}>
        <span style={{ fontSize: 8, fontWeight: 700, color: '#7a7a8e', flex: 1, textTransform: 'uppercase' }}>Item</span>
        <span style={{ fontSize: 8, fontWeight: 700, color: '#7a7a8e', width: 60, textAlign: 'center', textTransform: 'uppercase' }}>Conforms</span>
      </div>
      {items.map((item, i) => (
        <div key={item.id || i} style={{ display: 'flex', gap: 4, padding: '2px 0', borderBottom: '1px dotted #e0dbd0', alignItems: 'center' }}>
          <span style={{ fontSize: 8, color: '#1a1a2e', flex: 1, lineHeight: 1.3 }}>
            <span style={{ color: '#999', marginRight: 3 }}>{i + 1}.</span>
            {item.description || '—'}
          </span>
          <span style={{ fontSize: 9, width: 60, textAlign: 'center', color: item.conforms === 'Yes' ? '#22c55e' : item.conforms === 'No' ? '#ef4444' : '#ccc' }}>
            {item.conforms === 'Yes' ? '✓ Yes' : item.conforms === 'No' ? '✗ No' : '—'}
          </span>
        </div>
      ))}

      <H3>Outstanding Issues</H3>
      <P>{data.outstanding_issues || 'None.'}</P>

      <H3>Follow Up</H3>
      <KvRow label="Required" value={data.follow_up_required} />
      <KvRow label="When" value={data.follow_up_when} />

      <H3>Completed By</H3>
      <KvRow label="Name" value={data.completed_by_name} />
      <KvRow label="Position" value={data.completed_by_position} />
      <KvRow label="Date" value={data.completed_by_date} />
    </PdfPage>
  );
}

function Chapter029({ swmsData, companyInfo }) {
  const cn = companyInfo?.name || '[Company Name]';
  const policy = swmsData.injury_management_policy || {};

  const COMMITMENTS = [
    'prevent injury and illness by providing a safe and healthy working environment;',
    'participate in the development of an injury management plan and ensure that injury management commences as soon as possible after an employee is injured;',
    'support the injured employee and ensure that early return to work is a normal expectation;',
    'provide suitable duties for an injured employee as soon as possible;',
    'ensure that our injured employees (and anyone representing them) are aware of their rights and responsibilities – including the right to choose their own doctor and rehabilitation provider;',
    'consult with our employees and, where applicable, unions to ensure that the return-to-work program operates as smoothly as possible;',
    'maintain the confidentiality of injured employee\'s records.',
    'not dismiss an employee as a result of a work-related injury within six months of becoming unfit for employment.',
  ];

  return (
    <PdfPage chapterCode="029" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <ChapterHeading number="029" title="Injury Management & Return to Work" />
      <P>{cn} is committed to the return to work of injured employees.</P>
      <P style={{ fontWeight: 600, fontSize: 11, color: GOLD, marginTop: 6 }}>As part of this commitment, we will:</P>
      {COMMITMENTS.map((item, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 1 }}>
          <span style={{ color: GOLD, fontSize: 9 }}>•</span>
          <span style={{ fontSize: 9, color: '#1a1a2e', lineHeight: 1.4 }}>{item}</span>
        </div>
      ))}
      <div style={styles.divider} />
      <H3>Workplace Contact</H3>
      <KvRow label="Name" value={policy.workplace_contact?.name} />
      <KvRow label="Contact" value={policy.workplace_contact?.contact_details} />

      <H3>Scheme Agent</H3>
      <KvRow label="Name" value={policy.scheme_agent?.name} />
      <KvRow label="Organisation" value={policy.scheme_agent?.organisation} />

      {policy.rehab_providers?.length > 0 && (
        <div>
          <H3>Rehabilitation Providers</H3>
          {policy.rehab_providers.map((p, i) => (
            <div key={p.id || i} style={{ display: 'flex', gap: 6, marginBottom: 1 }}>
              <span style={{ fontSize: 9, color: '#1a1a2e' }}>• {p.name || '—'} ({p.organisation || '—'})</span>
            </div>
          ))}
        </div>
      )}
    </PdfPage>
  );
}

function Chapter030({ swmsData, companyInfo }) {
  const cn = companyInfo?.name || '[Company Name]';
  const entries = swmsData.signin_entries || [];

  if (entries.length === 0) {
    return (
      <PdfPage chapterCode="030" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
        <ChapterHeading number="030" title="Daily Sign In / Sign Out" />
        <P>No sign in/out records entered.</P>
      </PdfPage>
    );
  }

  const ENTRIES_PER_PAGE = 8;
  const pageCount = Math.max(1, Math.ceil(entries.length / ENTRIES_PER_PAGE));
  const pages = [];

  for (let p = 0; p < pageCount; p++) {
    const pageEntries = entries.slice(p * ENTRIES_PER_PAGE, (p + 1) * ENTRIES_PER_PAGE);
    pages.push(
      <PdfPage key={`ch030-p${p}`} chapterCode="030" companyName={companyInfo?.name} companyAbn={companyInfo?.acn} pageNum={p + 1} total={pageCount}>
        <ChapterHeading number="030" title="Daily Sign In / Sign Out" />
        <P>{cn} records all daily sign in and sign out entries in the following register.</P>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '18%' }}>Date</th>
              <th style={{ ...styles.th, width: '25%' }}>Name</th>
              <th style={{ ...styles.th, width: '15%' }}>Time In</th>
              <th style={{ ...styles.th, width: '15%' }}>Time Out</th>
              <th style={{ ...styles.th, width: '27%' }}>Signature</th>
            </tr>
          </thead>
          <tbody>
            {pageEntries.map(e => (
              <tr key={e.id}>
                <td style={styles.td}>{e.date || '—'}</td>
                <td style={styles.td}>{e.name || '—'}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace' }}>{e.time_in || '—'}</td>
                <td style={{ ...styles.td, fontFamily: 'monospace' }}>{e.time_out || '—'}</td>
                <td style={styles.td}>{e.signature || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </PdfPage>
    );
  }

  return pages;
}

function ChapterTOC({ companyInfo }) {
  const TOC_CATEGORIES = [
    { label: 'Document Control', chapters: [
      { code: '001', title: 'Document Control' },
    ]},
    { label: 'Company Information', chapters: [
      { code: '002', title: 'Project Details & Introduction' },
      { code: '003', title: 'OHSE & Environment Policy' },
    ]},
    { label: 'Risk Assessment', chapters: [
      { code: '004', title: 'Hazard Identification, Risk Assessment & Control' },
      { code: '005', title: 'Hazard Categories' },
      { code: '006', title: 'Risk Matrix' },
    ]},
    { label: 'Safe Work Method Statement', chapters: [
      { code: '007', title: 'Safe Work Method Statement (SWMS)' },
      { code: '008', title: 'Objectives & Targets' },
    ]},
    { label: 'PPE & Equipment', chapters: [
      { code: '009', title: 'Personal Protective Equipment (PPE)' },
    ]},
    { label: 'Training & Inspection', chapters: [
      { code: '010', title: 'Roles & Responsibilities' },
      { code: '011', title: 'Training & Competency Register' },
      { code: '012', title: 'Consultation & Communication' },
      { code: '013', title: 'Toolbox Talks / Pre-Start Briefings' },
      { code: '014', title: 'Workplace Inspection Checklist' },
    ]},
    { label: 'Hazard Management', chapters: [
      { code: '015', title: 'Plant & Equipment Policy' },
      { code: '016', title: 'Plant & Equipment Register' },
      { code: '017', title: 'Plant & Equipment Pre-Start Inspection' },
      { code: '018', title: 'Plant & Equipment Regular Checklist' },
      { code: '019', title: 'Hazardous Substances / Dangerous Goods' },
      { code: '020', title: 'Hazardous Substances / Dangerous Goods Register' },
      { code: '021', title: 'Electrical Equipment' },
      { code: '022', title: 'Electrical Equipment Register' },
      { code: '023', title: 'Hazard Reporting' },
      { code: '024', title: 'Hazard Report Form' },
    ]},
    { label: 'Incident Management', chapters: [
      { code: '025', title: 'Incident & Injury Investigation' },
      { code: '026', title: 'Register of Injuries' },
      { code: '027', title: 'Incident Investigation Report' },
    ]},
    { label: 'Administration', chapters: [
      { code: '028', title: 'OHSE Management Plan Checklist' },
      { code: '029', title: 'Injury Management & Return to Work' },
    ]},
    { label: 'Work Log', chapters: [
      { code: '030', title: 'Sign In / Sign Out' },
      { code: '031', title: 'Site Rules & Emergency Contacts' },
    ]},
  ];

  return (
    <PdfPage chapterCode="toc" companyName={companyInfo?.name} companyAbn={companyInfo?.acn}>
      <div style={{ marginTop: 6, marginBottom: 20 }}>
        <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1a1a2e', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>
          Contents
        </h2>
        <div style={{ width: 60, height: 2, background: GOLD }} />
      </div>
      <div>
        {TOC_CATEGORIES.map(cat => (
          <div key={cat.label} style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: GOLD, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 6, paddingBottom: 4, borderBottom: '1px solid rgba(201,168,76,0.3)' }}>
              {cat.label}
            </p>
            <div>
              {cat.chapters.map(ch => (
                <div key={ch.code} style={{ display: 'flex', alignItems: 'baseline', padding: '3px 0' }}>
                  <span style={{ fontSize: 10, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: GOLD, width: 32, flexShrink: 0 }}>{ch.code}</span>
                  <span style={{ fontSize: 13, color: '#1a1a2e', flexGrow: 1, borderBottom: '1px dotted rgba(26,26,46,0.15)', lineHeight: 1.6, paddingBottom: 1 }}>{ch.title}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </PdfPage>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Renders all SWMS chapters as A4 pages inside a hidden container.
 * The container has id="swms-pdf-preview" for the exporter to locate pages via [data-pdf-page].
 *
 * `<div id="swms-pdf-preview">` is the root element that the batch export modal
 * passes to capturePdfPages().
 */
export default function SwmsPdfPreview({ swmsData, projectInfo, companyInfo }) {
  useEffect(() => {
    const el = document.createElement('style');
    el.dataset.swms = '1';
    el.textContent = '#swms-pdf-preview table td, #swms-pdf-preview table th { vertical-align: middle !important; }';
    document.head.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);

  return (
    <div id="swms-pdf-preview">
      {/* Cover */}
      <CoverPage projectInfo={projectInfo} companyInfo={companyInfo} swmsData={swmsData} />

      {/* Table of Contents */}
      <ChapterTOC companyInfo={companyInfo} />

      {/* Ch001 — Document Control */}
      <Chapter001 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch002 — Project Details */}
      <ChapterProjectDetails projectInfo={projectInfo} companyInfo={companyInfo} swmsData={swmsData} />

      {/* Ch003 — OHSE Policy */}
      <Chapter003 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch004 — Hazard Identification */}
      <Chapter004 companyInfo={companyInfo} />

      {/* Ch005 — Hazard Categories */}
      <Chapter005 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch006 — Risk Matrix */}
      <Chapter006 companyInfo={companyInfo} />

      {/* Ch007 — SWMS Work Steps */}
      <Chapter007 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch008 — Objectives & Targets */}
      <Chapter008 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch009 — PPE */}
      <Chapter009 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch010 — Roles & Responsibilities */}
      <Chapter010 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch011 — Training Register */}
      <Chapter011 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch012 — Consultation */}
      <Chapter012 companyInfo={companyInfo} />

      {/* Ch013 — Toolbox Talks */}
      <Chapter013 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch014 — Workplace Inspections */}
      <Chapter014 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch015 — Plant & Equipment Policy */}
      <Chapter015 companyInfo={companyInfo} />

      {/* Ch016 — Equipment Register */}
      <Chapter016 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch017 — Pre-start Check */}
      <Chapter017 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch018 — Regular Check */}
      <Chapter018 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch019 — Hazardous Substances Policy */}
      <Chapter019 companyInfo={companyInfo} />

      {/* Ch020 — Hazardous Substance Register */}
      <Chapter020 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch021 — Electrical Equipment Policy */}
      <Chapter021 companyInfo={companyInfo} />

      {/* Ch022 — Electrical Equipment Register */}
      <Chapter022 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch023 — Hazard Reporting */}
      <Chapter023 companyInfo={companyInfo} />

      {/* Ch024 — Hazard Report Form */}
      <Chapter024 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch025 — Incident Policy */}
      <Chapter025 companyInfo={companyInfo} />

      {/* Ch026 — Injury Register */}
      <Chapter026 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch027 — Incident Investigation */}
      <Chapter027 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch028 — OHSE Management Plan Checklist */}
      <Chapter028 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch029 — Injury Management & Return to Work */}
      <Chapter029 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch030 — Daily Sign In / Sign Out */}
      <Chapter030 swmsData={swmsData} companyInfo={companyInfo} />

      {/* Ch031 — Site Rules + Emergency Contacts */}
      <Chapter031 swmsData={swmsData} companyInfo={companyInfo} />
    </div>
  );
}
